import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Drop-in replacement for useState that syncs with URL search params.
 * Usage: const [page, setPage] = useUrlState('page', 1);
 * On refresh, value is restored from URL.
 */
export const useUrlState = (key, defaultValue) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultRef = useRef(defaultValue);

  const getInitial = () => {
    const urlValue = searchParams.get(key);
    if (urlValue === null) return defaultValue;
    if (typeof defaultValue === 'number') return parseInt(urlValue) || defaultValue;
    return urlValue;
  };

  const [value, setValueState] = useState(getInitial);

  const setValue = useCallback((newValue) => {
    const resolved = typeof newValue === 'function' ? newValue(value) : newValue;
    setValueState(resolved);
  }, [value]);

  // Sync value to URL
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (value === defaultRef.current || value === '' || value === null || value === undefined) {
        p.delete(key);
      } else {
        p.set(key, String(value));
      }
      return p;
    }, { replace: true });
  }, [key, value, setSearchParams]);

  return [value, setValue];
};

export default useUrlState;
